import { HeaderIcons } from '../../shared/icons';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import Header from '../../shared/Header';
import { TimerBar, WaitingSpinner, getPlayerHeaderLeftButton } from '../../shared';
import {
  BonusLevelPayload,
  BonusGameType,
  BONUS_INTRO_MS,
  isBonusTimeUpSubmitType,
} from '../../../constants/bonusConstants';
import { getBonusChainCompleteMessages, getBonusIntro } from '../../../i18n/helpers';
import { useHostPlaying } from '../../../hooks/useHostPlaying';
import { buildBonusTimeUpFlushPayload, flushPointsForPlayerOverlay } from '../../../utils/bonusTimeUpFlush';
import { BonusIntro } from './BonusIntro';
import { WhosOnImagePlayer } from './whosOnImage/WhosOnImagePlayer';
import { WhosOnImageHost } from './whosOnImage/WhosOnImageHost';
import { WordOrderPlayer } from './wordOrder/WordOrderPlayer';
import { FastTypingHost } from './fastTyping/FastTypingHost';
import { FastTypingPlayer, type FastTypingPlayerHandle } from './fastTyping/FastTypingPlayer';
import { FastFingersPlayer } from './fastFingers/FastFingersPlayer';
import { CatchTheTrainPlayer } from './catchTheTrain/CatchTheTrainPlayer';
import { SaveTheAnimalsPlayer } from './saveTheAnimals/SaveTheAnimalsPlayer';
import { PuzzlePlayer } from './puzzle/PuzzlePlayer';
import { QuickMathPlayer } from './quickMath/QuickMathPlayer';
import { LetterStormPlayer } from './letterStorm/LetterStormPlayer';
import { LongestWordPlayer, type LongestWordPlayerHandle } from './longestWord/LongestWordPlayer';
import { getRandomPuzzleImageUrl } from '../../../utils/bonusPuzzleImage';
import { useTranslation } from '../../../i18n';
import '../../../styles/bonus-intro.css';
import '../../../styles/whos-on-image.css';

interface BonusLevelPlayerProps {
  roomState: {
    roomName: string;
    hostId: string;
    players: Array<{ id: string }>;
    isBonusLevel?: boolean;
    bonusLevelPayload?: BonusLevelPayload | null;
    bonusStartTime?: number;
    isPaused?: boolean;
  };
  socket: {
    emit: (event: string, data: any) => void;
    on: (event: string, cb: (data: any) => void) => void;
    off: (event: string, cb?: (data: any) => void) => void;
  };
  myPlayerId: string;
  onGoHome?: () => void;
  isHost?: boolean;
  onBackToGameSelection?: () => void;
}

interface BonusResult {
  questionId?: string;
  bonusGameType?: string;
  correctAnswer?: string;
  submissions?: Array<{ playerId: string; playerName: string; answer: string; points: number; isCorrect?: boolean }>;
  leaderboard?: Array<{ playerId: string; playerName: string; score: number }>;
}

/**
 * Player view during bonus.
 * When host is not playing: only the active game UI (no intro/result).
 * When host is playing: intro explain (BonusIntro + starting countdown) → game → result.
 *
 * Listens for bonus-level-result to freeze input, show overlay with correct
 * answer and points.  This provides the unified outro for all bonus games.
 */
export const BonusLevelPlayer: React.FC<BonusLevelPlayerProps> = ({
  roomState,
  socket,
  myPlayerId,
  onGoHome,
  isHost,
  onBackToGameSelection,
}) => {
  const { t } = useTranslation();
  const hostIsPlaying = useHostPlaying(roomState);
  const payload = roomState.bonusLevelPayload ?? null;
  /** Never fall back to Date.now() mid-round — that resets the bar on focus/room-state races. */
  const bonusStartTimeRef = useRef<number | null>(
    typeof roomState.bonusStartTime === 'number' ? roomState.bonusStartTime : null
  );
  const bonusStartFromServerRef = useRef(typeof roomState.bonusStartTime === 'number');
  if (typeof roomState.bonusStartTime === 'number') {
    if (!bonusStartFromServerRef.current) {
      bonusStartTimeRef.current = roomState.bonusStartTime;
      bonusStartFromServerRef.current = true;
    } else if (
      bonusStartTimeRef.current != null &&
      Math.abs(roomState.bonusStartTime - bonusStartTimeRef.current) > 500
    ) {
      // Server rewrote the clock after pause/resume — stay aligned with BE.
      bonusStartTimeRef.current = roomState.bonusStartTime;
    }
  } else if (payload?.questionId && bonusStartTimeRef.current == null) {
    bonusStartTimeRef.current = Date.now();
  }
  const bonusStartTime = bonusStartTimeRef.current ?? roomState.bonusStartTime ?? Date.now();
  const timeLimit = payload?.timeLimit ?? 30000;
  /** For timer-based games (fast-typing, fast-fingers): submit when round ends, i.e. intro + game time */
  const roundDurationMs = timeLimit + BONUS_INTRO_MS;

  // ── Local timer (syncs with host's timer for near-simultaneous "Time's Up") ──
  const [timeRemaining, setTimeRemaining] = useState(timeLimit);
  const [timeUp, setTimeUp] = useState(false);

  // ── Bonus result / round-ended state ──
  const [bonusResult, setBonusResult] = useState<BonusResult | null>(null);
  const [playerSubmitted, setPlayerSubmitted] = useState(false);
  const [myPointsFromAck, setMyPointsFromAck] = useState<number | undefined>(undefined);
  const [submittedPoints, setSubmittedPoints] = useState<number | undefined>(undefined);
  /** Remount word-order board after a rejected submit so the player can retry. */
  const [wordOrderUnlockNonce, setWordOrderUnlockNonce] = useState(0);
  /** Who's-on-image: last guess was wrong — keep input enabled and show hint. */
  const [whosWrongHint, setWhosWrongHint] = useState(false);
  const playerSubmittedRef = useRef(false);
  /** Latest fast-fingers score (updated via onScoreChange) */
  const fastFingersScoreRef = useRef(0);
  /** Latest catch-the-train score (updated via onScoreChange) */
  const catchTheTrainScoreRef = useRef(0);
  /** Latest save-the-animals score (updated via onScoreChange) */
  const saveTheAnimalsScoreRef = useRef(0);
  /** Latest fast-typing answer (updated via onAnswerChange) – for late-submit when bonus-level-result arrives without prior submit */
  const fastTypingAnswerRef = useRef('');
  /** DOM-backed answer read on time-up flush (IME-safe). */
  const fastTypingPlayerRef = useRef<FastTypingPlayerHandle | null>(null);
  /** Longest-word current slots word + imperative flush handle. */
  const longestWordAnswerRef = useRef('');
  const longestWordPlayerRef = useRef<LongestWordPlayerHandle | null>(null);
  /** Track the payload questionId so we can reset state on new bonus */
  const bonusQuestionIdRef = useRef<string | undefined>(payload?.questionId);
  /** Quick-math: finished all steps before timer; show same overlay UX as time-up with praise copy */
  const [quickMathChainComplete, setQuickMathChainComplete] = useState(false);
  /**
   * Once play has started for this bonus questionId, never re-show intro even if
   * bonusStartTime briefly jumps (defensive; BE should not reset the clock mid-round).
   */
  const [introPassed, setIntroPassed] = useState(false);
  /** True after we've observed the intro clock (remaining > timeLimit) for this round. */
  const seenIntroClockRef = useRef(false);
  /** When host is also a player, BonusHostScreen never mounts — host must broadcast puzzle image from here (once per question). */
  const hostPlayingPuzzleImageSentForRef = useRef<string | undefined>(undefined);
  /** Keep puzzle image URL for thumbnail when room-state briefly drops imageUrl (same idea as BonusLevelHost). */
  const puzzleThumbPersistRef = useRef<{ questionId: string; url: string } | null>(null);

  // Round ended = local timer hit 0 OR server result arrived
  const roundEnded = timeUp || !!bonusResult;

  /** Sent final score on result (fast-fingers) so we only send once */
  const lateFastFingersSentRef = useRef(false);
  /** Sent final score on result (catch-the-train) so we only send once */
  const lateCatchTheTrainSentRef = useRef(false);
  /** Sent final score on result (save-the-animals) so we only send once */
  const lateSaveTheAnimalsSentRef = useRef(false);
  /** Sent late answer on result (fast-typing) so we only send once */
  const lateFastTypingSentRef = useRef(false);
  const lateLongestWordSentRef = useRef(false);
  /** Parent already emitted authoritative time-up flush for this round (arcade may re-flush). */
  const timeUpFlushSentRef = useRef(false);

  // Reset state when a new bonus round starts
  useEffect(() => {
    if (payload?.questionId && payload.questionId !== bonusQuestionIdRef.current) {
      bonusQuestionIdRef.current = payload.questionId;
      bonusStartTimeRef.current =
        typeof roomState.bonusStartTime === 'number' ? roomState.bonusStartTime : null;
      bonusStartFromServerRef.current = typeof roomState.bonusStartTime === 'number';
      hostPlayingPuzzleImageSentForRef.current = undefined;
      setTimeUp(false);
      setTimeRemaining(payload.timeLimit ?? 30000);
      setBonusResult(null);
      setPlayerSubmitted(false);
      setMyPointsFromAck(undefined);
      setSubmittedPoints(undefined);
      playerSubmittedRef.current = false;
      fastFingersScoreRef.current = 0;
      catchTheTrainScoreRef.current = 0;
      saveTheAnimalsScoreRef.current = 0;
      fastTypingAnswerRef.current = '';
      longestWordAnswerRef.current = '';
      lateFastFingersSentRef.current = false;
      lateCatchTheTrainSentRef.current = false;
      lateSaveTheAnimalsSentRef.current = false;
      lateFastTypingSentRef.current = false;
      lateLongestWordSentRef.current = false;
      timeUpFlushSentRef.current = false;
      setQuickMathChainComplete(false);
      setIntroPassed(false);
      seenIntroClockRef.current = false;
      setWordOrderUnlockNonce(0);
      setWhosWrongHint(false);
    }
  }, [payload?.questionId, payload?.timeLimit, roomState.bonusStartTime]);

  // Only lock out intro after we've actually been in the intro window, then left it.
  // (Initial timeRemaining === timeLimit must not mark intro as passed before bonusStartTime syncs.)
  useEffect(() => {
    if (timeRemaining > timeLimit) {
      seenIntroClockRef.current = true;
      return;
    }
    if (seenIntroClockRef.current && !introPassed) {
      setIntroPassed(true);
    }
  }, [timeRemaining, timeLimit, introPassed]);

  // Host + playing: pick puzzle image and broadcast (normally done from BonusHostScreen intro).
  useEffect(() => {
    if (!isHost || !hostIsPlaying) return;
    if (!payload?.questionId || payload.bonusGameType !== 'puzzle') return;
    if (!roomState.roomName) return;
    if (hostPlayingPuzzleImageSentForRef.current === payload.questionId) return;
    hostPlayingPuzzleImageSentForRef.current = payload.questionId;
    const imageUrl = getRandomPuzzleImageUrl();
    socket.emit('bonus-puzzle-set-image', { roomName: roomState.roomName, imageUrl });
  }, [isHost, hostIsPlaying, payload?.bonusGameType, payload?.questionId, roomState.roomName, socket]);

  // Host + playing: preload who's-on-image during intro (normally done in BonusLevelHost).
  useEffect(() => {
    if (!hostIsPlaying || payload?.bonusGameType !== 'whos-on-image') return;
    const primary = payload.imageUrl;
    const fallback = payload.fallbackImageUrl;
    if (primary) {
      const img = new Image();
      img.src = primary;
    }
    if (fallback) {
      const img = new Image();
      img.src = fallback;
    }
  }, [hostIsPlaying, payload?.bonusGameType, payload?.imageUrl, payload?.fallbackImageUrl]);

  // Tick timer: backend ends at bonusStartTime + BONUS_INTRO_MS + timeLimit
  // Player sees game immediately (no intro), so remaining = until backend ends.
  // Freeze while paused so local time-up does not flush ahead of the server clock.
  useEffect(() => {
    if (!payload || timeUp) return;
    if (roomState.isPaused) return;
    const interval = setInterval(() => {
      const roundEndTime = bonusStartTime + BONUS_INTRO_MS + timeLimit;
      const remaining = Math.max(0, roundEndTime - Date.now());
      setTimeRemaining(remaining);
      if (remaining <= 0) {
        setTimeUp(true);
        // Local time-up flush — covered again by server bonus-time-up; both are safe.
        flushTimeUpSubmissionRef.current();
        clearInterval(interval);
      }
    }, 200);
    return () => clearInterval(interval);
  }, [payload, bonusStartTime, timeLimit, timeUp, roomState.isPaused]);

  // Listen for bonus-time-up: server says game over → authoritative flush, then freeze UI.
  // Grace extension re-emits this; arcade games re-flush latest points.
  useEffect(() => {
    const onTimeUp = () => {
      setTimeUp(true);
      flushTimeUpSubmissionRef.current();
    };
    socket.on('bonus-time-up', onTimeUp);
    return () => { socket.off('bonus-time-up', onTimeUp); };
  }, [socket]);

  // Listen for bonus-level-result (arrives after grace period)
  useEffect(() => {
    const onResult = (data: BonusResult) => {
      setTimeUp(true); // ensure overlay is shown
      setBonusResult(data);
      // Last-chance flush if parent/child paths missed (e.g. reconnect mid-outro)
      flushTimeUpSubmissionRef.current();

      // Fast-fingers: if BE didn't have our score (0 in result), send final score once so BE/host can update
      if (data?.bonusGameType === 'fast-fingers' && !lateFastFingersSentRef.current) {
        const mySub = data?.submissions?.find((s) => s.playerId === myPlayerId);
        const myPointsInResult = mySub?.points ?? 0;
        const localScore = fastFingersScoreRef.current;
        if (localScore > 0 && myPointsInResult === 0) {
          lateFastFingersSentRef.current = true;
          socket.emit('bonus-answer', {
            roomName: roomState.roomName,
            answer: 'time-up',
            points: localScore,
          });
        }
      }

      // Catch-the-train: if BE didn't have our score (0 in result), send final score once so BE/host can update
      if (data?.bonusGameType === 'catch-the-train' && !lateCatchTheTrainSentRef.current) {
        const mySub = data?.submissions?.find((s) => s.playerId === myPlayerId);
        const myPointsInResult = mySub?.points ?? 0;
        const localPoints = catchTheTrainScoreRef.current;
        if (localPoints > 0 && myPointsInResult === 0) {
          lateCatchTheTrainSentRef.current = true;
          socket.emit('bonus-answer', {
            roomName: roomState.roomName,
            answer: 'time-up',
            points: localPoints,
          });
        }
      }

      // Save-the-animals: if BE didn't have our score (0 in result), send final score once so BE/host can update
      if (data?.bonusGameType === 'catch-me-if-you-can' && !lateSaveTheAnimalsSentRef.current) {
        const mySub = data?.submissions?.find((s) => s.playerId === myPlayerId);
        const myPointsInResult = mySub?.points ?? 0;
        const localPoints = saveTheAnimalsScoreRef.current;
        if (localPoints > 0 && myPointsInResult === 0) {
          lateSaveTheAnimalsSentRef.current = true;
          socket.emit('bonus-answer', {
            roomName: roomState.roomName,
            answer: 'time-up',
            points: localPoints,
          });
        }
      }

      // Fast-typing: recover even if an earlier empty/time-up flush locked FE submit-once
      if (data?.bonusGameType === 'fast-typing' && !lateFastTypingSentRef.current) {
        const mySub = data?.submissions?.find((s) => s.playerId === myPlayerId);
        const myPointsInResult = mySub?.points ?? 0;
        const domAnswer = fastTypingPlayerRef.current?.getAnswer();
        if (typeof domAnswer === 'string' && domAnswer.length > 0) {
          fastTypingAnswerRef.current = domAnswer;
        }
        const localAnswer = fastTypingAnswerRef.current?.trim() ?? '';
        if (localAnswer.length > 0 && myPointsInResult === 0) {
          lateFastTypingSentRef.current = true;
          socket.emit('bonus-answer', {
            roomName: roomState.roomName,
            answer: localAnswer,
            points: 0, // BE will compute points from answer
          });
        }
      }

      if (data?.bonusGameType === 'longest-word' && !lateLongestWordSentRef.current) {
        const mySub = data?.submissions?.find((s) => s.playerId === myPlayerId);
        const myPointsInResult = mySub?.points ?? 0;
        const domAnswer = longestWordPlayerRef.current?.getAnswer();
        if (typeof domAnswer === 'string' && domAnswer.length > 0) {
          longestWordAnswerRef.current = domAnswer;
        }
        const localAnswer = longestWordAnswerRef.current?.trim() ?? '';
        if (localAnswer.length > 0 && myPointsInResult === 0) {
          lateLongestWordSentRef.current = true;
          socket.emit('bonus-answer', {
            roomName: roomState.roomName,
            answer: localAnswer,
            points: 0,
          });
        }
      }
    };
    socket.on('bonus-level-result', onResult);
    return () => { socket.off('bonus-level-result', onResult); };
  }, [socket, roomState.roomName, myPlayerId]);

  // Listen for bonus-answer-ack (fast-typing, fast-fingers, quick-math, word-order, who's-on-image)
  useEffect(() => {
    const onAck = (data: { points?: number; incorrect?: boolean }) => {
      if (data?.incorrect === true) {
        // Wrong guess (who's-on-image): unlock and show try-again — do not lock the round.
        playerSubmittedRef.current = false;
        setPlayerSubmitted(false);
        setSubmittedPoints(undefined);
        setMyPointsFromAck(undefined);
        setWhosWrongHint(true);
        return;
      }
      setWhosWrongHint(false);
      if (typeof data.points === 'number') setMyPointsFromAck(data.points);
    };
    const onRejected = () => {
      // Ignore stale rejects after we already have points (idempotent re-submit should ack, not reject).
      if (myPointsFromAck !== undefined || submittedPoints !== undefined) return;
      // Unlock so player can retry (e.g. stale socket mid-bonus before any successful score).
      playerSubmittedRef.current = false;
      setPlayerSubmitted(false);
      setSubmittedPoints(undefined);
      setMyPointsFromAck(undefined);
      setWordOrderUnlockNonce((n) => n + 1);
      setWhosWrongHint(false);
    };
    socket.on('bonus-answer-ack', onAck);
    socket.on('bonus-answer-rejected', onRejected);
    return () => {
      socket.off('bonus-answer-ack', onAck);
      socket.off('bonus-answer-rejected', onRejected);
    };
  }, [socket, myPointsFromAck, submittedPoints]);

  // ── Submit handler ──
  const handleBonusComplete = useCallback((answer: string, points?: number) => {
    const isTypingUpgrade = payload?.bonusGameType === 'fast-typing' && playerSubmittedRef.current;
    if (playerSubmittedRef.current && !isTypingUpgrade) return;
    playerSubmittedRef.current = true;
    setPlayerSubmitted(true);
    setSubmittedPoints(points);
    if (payload?.bonusGameType === 'whos-on-image') {
      setWhosWrongHint(false);
    }
    socket.emit('bonus-answer', {
      roomName: roomState.roomName,
      answer,
      points: points ?? 0,
    });
  }, [socket, roomState.roomName, payload?.bonusGameType]);

  /**
   * Authoritative end-of-round flush for timer/arcade bonuses.
   * Called on server bonus-time-up (including grace re-nudge) and local time-up so
   * scoring does not depend only on each game child's disabled effect.
   */
  const flushTimeUpSubmission = useCallback(() => {
    const gameType = payload?.bonusGameType;
    if (!isBonusTimeUpSubmitType(gameType)) return;

    // Prefer live textarea value so IME/composition does not flush a stale empty ref.
    if (gameType === 'fast-typing') {
      const domAnswer = fastTypingPlayerRef.current?.getAnswer();
      if (typeof domAnswer === 'string') {
        fastTypingAnswerRef.current = domAnswer;
      }
    }
    if (gameType === 'longest-word') {
      const domAnswer = longestWordPlayerRef.current?.getAnswer();
      if (typeof domAnswer === 'string') {
        longestWordAnswerRef.current = domAnswer;
      }
    }

    const flush = buildBonusTimeUpFlushPayload(gameType, {
      fastFingers: fastFingersScoreRef.current,
      catchTheTrain: catchTheTrainScoreRef.current,
      saveTheAnimals: saveTheAnimalsScoreRef.current,
      fastTypingAnswer: fastTypingAnswerRef.current,
      longestWordAnswer: longestWordAnswerRef.current,
    });
    if (!flush) return;

    if (!flush.allowResend && (playerSubmittedRef.current || timeUpFlushSentRef.current)) {
      return;
    }
    // Typing / longest-word: skip duplicate empty flushes after we already registered a real answer.
    if (
      (gameType === 'fast-typing' || gameType === 'longest-word') &&
      playerSubmittedRef.current &&
      flush.answer === 'time-up' &&
      ((gameType === 'fast-typing'
        ? fastTypingAnswerRef.current?.trim()
        : longestWordAnswerRef.current?.trim()) ?? ''
      ).length === 0
    ) {
      return;
    }

    timeUpFlushSentRef.current = true;
    playerSubmittedRef.current = true;
    setPlayerSubmitted(true);
    // Typing points come from bonus-answer-ack only (flush.points is always 0).
    const overlayPts = flushPointsForPlayerOverlay(gameType, flush);
    if (overlayPts !== undefined) {
      setSubmittedPoints(overlayPts);
    }
    socket.emit('bonus-answer', {
      roomName: roomState.roomName,
      answer: flush.answer,
      points: flush.points,
    });
  }, [payload?.bonusGameType, socket, roomState.roomName]);

  const flushTimeUpSubmissionRef = useRef(flushTimeUpSubmission);
  useEffect(() => {
    flushTimeUpSubmissionRef.current = flushTimeUpSubmission;
  }, [flushTimeUpSubmission]);
  /** Track fast-fingers score for overlay display */
  const handleFastFingersScoreChange = useCallback((score: number) => {
    fastFingersScoreRef.current = score;
  }, []);

  /** Track catch-the-train score for overlay and late submit */
  const handleCatchTheTrainScoreChange = useCallback((score: number) => {
    catchTheTrainScoreRef.current = score;
  }, []);

  /** Track save-the-animals score for overlay and late submit */
  const handleSaveTheAnimalsScoreChange = useCallback((score: number) => {
    saveTheAnimalsScoreRef.current = score;
  }, []);

  /** Track fast-typing answer for late-submit when bonus-level-result arrives without prior submit */
  const handleFastTypingAnswerChange = useCallback((answer: string) => {
    fastTypingAnswerRef.current = answer;
  }, []);

  const handleLongestWordAnswerChange = useCallback((answer: string) => {
    longestWordAnswerRef.current = answer;
  }, []);

  // ── Find my submission from result ──
  const mySubmission = bonusResult?.submissions?.find((s) => s.playerId === myPlayerId);
  const bonusGameType = payload?.bonusGameType ?? bonusResult?.bonusGameType;
  // Submit-once / puzzle: never show client-provisional points — overlay "+N" must match BE ack
  // (or result submission). Arcade may use local flush score until ack arrives.
  const myPoints =
    mySubmission?.points ??
    myPointsFromAck ??
    (bonusGameType === 'puzzle' ||
    bonusGameType === 'word-order' ||
    bonusGameType === 'whos-on-image'
      ? undefined
      : submittedPoints);

  const quickMathPraise = useMemo(() => {
    const list = getBonusChainCompleteMessages();
    if (!list.length) return { title: t('bonus.chainComplete.wellDone.title'), subtitle: t('bonus.player.chainCompleteFallback') };
    const pick = Math.floor(Math.random() * list.length);
    return list[pick];
  }, [payload?.questionId]);

  /** Until next question, keep praise UX (avoid swapping to "Time's Up" after they already cleared the chain). */
  const showQuickMathChainOverlay = bonusGameType === 'quick-math' && quickMathChainComplete;

  // Show overlay with points as soon as player submitted (math-like feedback).
  // Fast-typing: show immediately on submit; points fill in from ack.
  // Word-order: wait for ack so green flash is visible (Puzzle pattern); ack is idempotent on retry.
  // Arcade: wait for ack (or round end uses local score). Guess games: points from ack/result.
  const showEarlyResult =
    playerSubmitted &&
    (bonusGameType === 'fast-typing' ||
      bonusGameType === 'longest-word' ||
      (((bonusGameType === 'fast-fingers' ||
        bonusGameType === 'catch-the-train' ||
        bonusGameType === 'catch-me-if-you-can') &&
        myPointsFromAck !== undefined) ||
        (bonusGameType === 'puzzle' && myPoints !== undefined) ||
        ((bonusGameType === 'whos-on-image' || bonusGameType === 'word-order') &&
          myPoints !== undefined)));

  // ── Game content ──
  // Longest-word stays editable until round end / flush (do not freeze on playerSubmitted alone mid-play).
  const isDisabled =
    roundEnded || (playerSubmitted && bonusGameType !== 'longest-word');

  const roundEndTimeMs = bonusStartTime + BONUS_INTRO_MS + timeLimit;

  /** Intro still running: timer bar includes BONUS_INTRO_MS before play clock counts down from timeLimit. */
  const isPuzzleIntro =
    payload?.bonusGameType === 'puzzle' && !roundEnded && timeRemaining > timeLimit;
  const puzzleIntroCountdownSeconds =
    hostIsPlaying && isPuzzleIntro
      ? Math.max(0, Math.ceil((timeRemaining - timeLimit) / 1000))
      : null;

  /** Seconds left in bonus intro (all clients); used when host plays so there is no cast intro/countdown. */
  const bonusIntroSecondsLeft =
    !roundEnded && timeRemaining > timeLimit
      ? Math.max(0, Math.ceil((timeRemaining - timeLimit) / 1000))
      : null;

  const showHostPlayingIntro =
    hostIsPlaying &&
    !roundEnded &&
    !introPassed &&
    bonusIntroSecondsLeft != null &&
    bonusIntroSecondsLeft > 0 &&
    !!payload;

  const isLastBonusRound =
    payload?.isLastBonusRound ??
    (payload?.questionNumber != null &&
      payload?.totalQuestions != null &&
      payload.questionNumber >= payload.totalQuestions);

  const hostPlayingIntroBannerEl =
    showHostPlayingIntro ? (
      <div
        role="status"
        aria-live="polite"
        style={{
          textAlign: 'center',
          padding: '0.85rem 1rem 0.35rem',
          marginBottom: '0.15rem',
          flexShrink: 0,
        }}
      >
        <span
          style={{
            fontSize: 'clamp(1.45rem, 6.5vw, 2.2rem)',
            fontWeight: 800,
            lineHeight: 1.15,
            color: 'var(--color-primary)',
            textShadow: '0 0 20px rgba(var(--color-primary-rgb), 0.35)',
          }}
        >
          {t('bonus.player.startingIn', { seconds: bonusIntroSecondsLeft })}
        </span>
      </div>
    ) : null;

  const hostPlayingIntroExplainEl =
    showHostPlayingIntro && payload ? (
      <BonusIntro
        bonusGameType={(payload.bonusGameType ?? 'puzzle') as BonusGameType}
        isLastRound={isLastBonusRound}
        payload={payload}
      />
    ) : null;

  if (payload?.bonusGameType === 'puzzle' && payload.questionId && payload.imageUrl) {
    puzzleThumbPersistRef.current = { questionId: payload.questionId, url: payload.imageUrl };
  }
  const puzzleReferenceImageUrl =
    payload?.bonusGameType === 'puzzle'
      ? (payload.imageUrl ||
          (puzzleThumbPersistRef.current?.questionId === payload?.questionId
            ? puzzleThumbPersistRef.current.url
            : ''))
      : '';

  const wordOrderTarget = payload?.correctAnswer ?? '';
  const wordOrderLen = Math.max(wordOrderTarget.length, 1);
  /** Tighter vw on 7+ letters so the host preview fits iPhone mini without horizontal scroll */
  const wordOrderVwHint = Math.min(12, (wordOrderLen >= 7 ? 70 : 78) / wordOrderLen);

  const puzzlePlayerEl = (
    <PuzzlePlayer
      imageUrl={payload?.imageUrl ?? ''}
      roundEndTimeMs={roundEndTimeMs}
      timeLimitMs={timeLimit}
      onSubmit={(answer, points) => handleBonusComplete(answer, points)}
      disabled={isDisabled}
      isIntro={isPuzzleIntro}
      footerHint={
        hostIsPlaying ? t('bonus.player.puzzleHint') : undefined
      }
      introCountdownSeconds={hostIsPlaying ? puzzleIntroCountdownSeconds : null}
    />
  );

  const wordOrderPlayerEl =
    payload?.bonusGameType === 'word-order' ? (
      <WordOrderPlayer
        key={`${payload.questionId ?? 'word-order'}-${wordOrderUnlockNonce}`}
        correctAnswer={payload.correctAnswer ?? ''}
        onSubmit={(answer) => handleBonusComplete(answer)}
        disabled={isDisabled}
      />
    ) : null;

  const gameContent =
    payload?.bonusGameType === 'puzzle' ? (
      hostIsPlaying ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            gap: '0.75rem',
          }}
        >
          {puzzleReferenceImageUrl ? (
            <img
              src={puzzleReferenceImageUrl}
              alt=""
              style={{
                maxHeight: 'min(32vw, 160px)',
                maxWidth: 'min(88vw, 340px)',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                borderRadius: 10,
                border: '2px solid var(--color-primary)',
                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.25)',
              }}
            />
          ) : null}
          {puzzlePlayerEl}
        </div>
      ) : (
        puzzlePlayerEl
      )
    ) : payload?.bonusGameType === 'whos-on-image' ? (
      <div className="whos-on-image-play">
        {/* Image stage is rendered at scroll-root level when host is playing (behind timer). */}
        <div className="whos-on-image-play__form">
          <WhosOnImagePlayer
            imageUrl={payload.imageUrl ?? ''}
            fallbackImageUrl={payload.fallbackImageUrl}
            timeLimit={payload.timeLimit ?? 25000}
            onSubmit={(answer) => handleBonusComplete(answer)}
            disabled={isDisabled}
            incorrectHint={whosWrongHint}
          />
        </div>
      </div>
    ) : payload?.bonusGameType === 'word-order' ? (
      hostIsPlaying ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            gap: '1rem',
          }}
        >
          <div
            style={{
              textAlign: 'center',
              width: '100%',
              padding: '0 max(0.5rem, env(safe-area-inset-right)) 0 max(0.5rem, env(safe-area-inset-left))',
              boxSizing: 'border-box',
            }}
          >
            <span
              style={{
                fontSize: `clamp(1.1rem, ${wordOrderVwHint}vw, 2.5rem)`,
                fontWeight: 'bold',
                letterSpacing:
                  wordOrderLen >= 9 ? '0.04em' : wordOrderLen >= 7 ? '0.05em' : wordOrderLen > 12 ? '0.06em' : '0.1em',
                color: 'var(--color-primary)',
                wordBreak: 'break-word',
                overflowWrap: 'anywhere',
              }}
            >
              {wordOrderTarget}
            </span>
          </div>
          {wordOrderPlayerEl}
        </div>
      ) : (
        wordOrderPlayerEl
      )
    ) : payload?.bonusGameType === 'fast-typing' ? (
      hostIsPlaying ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            gap: '0.75rem',
          }}
        >
          {/* Same typewriter as cast host; mount only after intro so reveal starts with play clock */}
          {!showHostPlayingIntro ? (
            <FastTypingHost
              text={payload.text ?? ''}
              timeLimit={payload.timeLimit ?? 30000}
              roundEnded={roundEnded}
              soundEnabled={false}
              compact
            />
          ) : null}
          <FastTypingPlayer
            ref={fastTypingPlayerRef}
            timeLimit={roundDurationMs}
            onSubmit={(answer) => handleBonusComplete(answer)}
            onAnswerChange={handleFastTypingAnswerChange}
            disabled={isDisabled}
            compact
          />
        </div>
      ) : (
        <FastTypingPlayer
          ref={fastTypingPlayerRef}
          timeLimit={roundDurationMs}
          onSubmit={(answer) => handleBonusComplete(answer)}
          onAnswerChange={handleFastTypingAnswerChange}
          disabled={isDisabled}
        />
      )
    ) : payload?.bonusGameType === 'fast-fingers' ? (
      <FastFingersPlayer
        successEmoji={payload.successEmoji ?? '⚡'}
        wrongEmojis={payload.wrongEmojis ?? ['❌']}
        totalCount={payload.totalCount ?? 140}
        successCount={payload.successCount ?? 70}
        successPoints={payload.successPoints ?? 10}
        wrongPenalty={payload.wrongPenalty ?? 5}
        timeLimit={roundDurationMs}
        onSubmit={(answer, points) => handleBonusComplete(answer, points)}
        onScoreChange={handleFastFingersScoreChange}
        disabled={roundEnded}
      />
    ) : payload?.bonusGameType === 'catch-the-train' ? (
      <CatchTheTrainPlayer
        themeTitle={payload.themeTitle ?? getBonusIntro('catch-the-train').title}
        emoji={payload.emoji ?? '🚂'}
        timeLimit={roundDurationMs}
        onSubmit={(answer, points) => handleBonusComplete(answer, points)}
        onScoreChange={handleCatchTheTrainScoreChange}
        disabled={roundEnded}
      />
    ) : payload?.bonusGameType === 'catch-me-if-you-can' ? (
      <SaveTheAnimalsPlayer
        themeTitle={payload.themeTitle ?? getBonusIntro('catch-me-if-you-can').title}
        animals={payload.animals ?? ['🐱', '🐕', '🐦']}
        timeLimit={roundDurationMs}
        onSubmit={(answer, points) => handleBonusComplete(answer, points)}
        onScoreChange={handleSaveTheAnimalsScoreChange}
        disabled={roundEnded}
      />
    ) : payload?.bonusGameType === 'quick-math' ? (
      <QuickMathPlayer
        roomName={roomState.roomName}
        questionId={payload.questionId}
        steps={payload.steps ?? []}
        disabled={roundEnded}
        bonusIntroEndsAt={bonusStartTime + BONUS_INTRO_MS}
        introSecondsLeft={hostIsPlaying ? bonusIntroSecondsLeft : null}
        onChainComplete={() => setQuickMathChainComplete(true)}
        socket={socket}
      />
    ) : payload?.bonusGameType === 'letter-storm' ? (
      <LetterStormPlayer
        roomName={roomState.roomName}
        letter={payload.letter ?? '?'}
        minWordLength={payload.minWordLength ?? 3}
        maxWordLength={payload.maxWordLength ?? 12}
        disabled={isDisabled}
        showLetter={hostIsPlaying}
        onPointsChange={setMyPointsFromAck}
        socket={socket}
      />
    ) : payload?.bonusGameType === 'longest-word' ? (
      <LongestWordPlayer
        ref={longestWordPlayerRef}
        letters={payload.letters ?? ''}
        disabled={roundEnded}
        isIntro={!roundEnded && timeRemaining > timeLimit}
        onAnswerChange={handleLongestWordAnswerChange}
      />
    ) : (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p style={{ marginBottom: '1rem' }}>{t('bonus.unknownGame', { type: payload?.bonusGameType ?? '—' })}</p>
        <button
          type="button"
          onClick={() => handleBonusComplete('Done', 100)}
          disabled={isDisabled}
          style={{
            padding: '0.75rem 1.5rem',
            fontSize: '1.1rem',
            borderRadius: 8,
            background: 'var(--color-primary)',
            color: '#fff',
            border: 'none',
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            opacity: isDisabled ? 0.5 : 1,
          }}
        >
          {t('bonus.player.imDone')}
        </button>
      </div>
    );

  // ── Points display value (works for all game types) ──
  // Arcade scores are visible and accumulated locally throughout play. A result
  // payload can briefly contain 0 when the final submission and grace-end cross;
  // keep showing the gathered score while the late update reaches the server.
  const localArcadePoints =
    bonusGameType === 'fast-fingers'
      ? fastFingersScoreRef.current
      : bonusGameType === 'catch-the-train'
        ? catchTheTrainScoreRef.current
        : bonusGameType === 'catch-me-if-you-can'
          ? saveTheAnimalsScoreRef.current
          : undefined;
  const displayPoints =
    localArcadePoints !== undefined
      ? Math.max(localArcadePoints, myPoints ?? 0)
      : myPoints ??
        (bonusGameType === 'quick-math' ||
        bonusGameType === 'letter-storm' ||
        bonusGameType === 'longest-word'
          ? myPointsFromAck
          : undefined);

  // ── Timer progress bar (slider only; no seconds — BE/host are source of truth). ──
  // Cap at timeLimit so intro (remaining > timeLimit) keeps a full bar, then depletes in play.
  // Sticky so fast-typing / keyboard scroll does not hide the only time cue when host is playing.
  const playTimeRemaining = Math.min(timeRemaining, timeLimit);
  const showOverlayInsteadOfGame = roundEnded || showEarlyResult || showQuickMathChainOverlay;
  const timerBar = !showOverlayInsteadOfGame && (
    <div className="bonus-player__timer">
      <TimerBar
        timeRemaining={playTimeRemaining}
        timeLimit={timeLimit}
        questionNumber={payload?.questionNumber}
        totalQuestions={payload?.totalQuestions}
        showTimeCounter={false}
        showQuestionLabel={false}
      />
    </div>
  );

  // ── End-of-round content: shown when local timer hits 0 (synced with host) ──
  // Submit-based games (whos-on-image, word-order) show correct answer + points.
  // Timer-based games (fast-fingers, fast-typing) show "Time's Up" + points.
  const correctAnswer = bonusResult?.correctAnswer ?? payload?.correctAnswer;
  const isSubmitGame = bonusGameType === 'whos-on-image' || bonusGameType === 'word-order' || bonusGameType === 'puzzle';
  const myFlushedWord =
    (mySubmission?.answer && mySubmission.answer !== 'time-up' && mySubmission.answer !== 'Done'
      ? mySubmission.answer
      : longestWordAnswerRef.current) || '';
  const longestWordTooShort =
    bonusGameType === 'longest-word' &&
    (displayPoints ?? 0) <= 0 &&
    myFlushedWord.replace(/[^A-Za-z]/g, '').length > 0 &&
    myFlushedWord.replace(/[^A-Za-z]/g, '').length < 3;
  const showLongestWordIdeal =
    bonusGameType === 'longest-word' && !!correctAnswer && (roundEnded || showEarlyResult);
  /** For puzzle: solved once we have positive points; pending while submitted but ack not yet in. */
  const puzzleSolved = bonusGameType === 'puzzle' && (myPoints ?? 0) > 0;
  const puzzlePendingResult =
    bonusGameType === 'puzzle' && playerSubmitted && (myPoints === undefined || myPoints <= 0) && !roundEnded;
  const puzzlePraiseOptions = useMemo(
    () => [
      t('bonus.chainComplete.wellDone.title'),
      t('bonus.chainComplete.awesome.title'),
      t('bonus.chainComplete.youAreAwesome.title'),
      t('bonus.chainComplete.nailedIt.title'),
      t('bonus.chainComplete.legend.title'),
      t('bonus.chainComplete.geniusMode.title'),
      t('bonus.chainComplete.cleanSweep.title'),
      t('bonus.chainComplete.sharp.title'),
      t('gameplay.speedChallenge.rankFeedback.5.0'),
    ].filter(Boolean),
    [t]
  );
  const puzzlePraiseTitle = useMemo(() => {
    if (!puzzlePraiseOptions.length) return t('bonus.chainComplete.wellDone.title');
    return puzzlePraiseOptions[Math.floor(Math.random() * puzzlePraiseOptions.length)];
  }, [payload?.questionId, puzzlePraiseOptions, t]);
  const showPuzzleSolvedOverlay =
    bonusGameType === 'puzzle' && playerSubmitted && (myPoints ?? 0) > 0;

  const correctAnswerLen = Math.max((correctAnswer ?? '').length, 1);
  /** Long answers (e.g. word-order) shrink on narrow screens so the overlay does not overflow horizontally */
  const correctAnswerOverlayVw = Math.min(
    11,
    (correctAnswerLen >= 7 ? 72 : 82) / correctAnswerLen
  );

  const waitingMessage = !roundEnded
    ? t('gameplay.loading.waitingForOthers')
    : hostIsPlaying
      ? t('bonus.player.roundOverWaiting')
      : t('bonus.player.roundOverWatchHost');

  const pointsBlock = (
    <>
      {displayPoints !== undefined && displayPoints > 0 ? (
        <p style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#34D399', margin: '0 0 0.75rem' }}>
          +{displayPoints} {t('common.pts')}
        </p>
      ) : displayPoints !== undefined && displayPoints <= 0 ? (
        <p style={{ fontSize: '1.2rem', color: 'rgba(255,255,255,0.5)', margin: '0 0 0.75rem' }}>
          {displayPoints} {t('common.pts')}
        </p>
      ) : playerSubmitted && !showEarlyResult ? (
        // Avoid duplicating the "Answer submitted!" title already shown in the early-result header.
        <p style={{ fontSize: '1.1rem', color: 'rgba(255,255,255,0.5)', margin: '0 0 0.75rem' }}>
          {t('bonus.player.answerSubmitted')}
        </p>
      ) : null}
    </>
  );

  const overlay = (roundEnded || showEarlyResult || showQuickMathChainOverlay) && (
    <div
      className="bonus-player-result-overlay"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(15, 23, 42, 0.92)',
        borderRadius: '12px',
        padding: '1.75rem max(1rem, env(safe-area-inset-right)) 1.75rem max(1rem, env(safe-area-inset-left))',
        marginTop: '0.75rem',
        textAlign: 'center',
        animation: 'fadeIn 0.3s ease-out',
        minHeight: 'auto',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      {showPuzzleSolvedOverlay ? (
        <>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🎉</div>
          <h2 style={{ fontSize: '1.6rem', fontWeight: 'bold', color: '#F8FAFC', marginBottom: '0.35rem' }}>
            {puzzlePraiseTitle}
          </h2>
          <p
            style={{
              fontSize: '1rem',
              color: 'rgba(248, 250, 252, 0.82)',
              margin: '0 0 1rem',
              maxWidth: '22rem',
              lineHeight: 1.45,
            }}
          >
            {t('bonus.player.puzzleSolved')}
          </p>
          {pointsBlock}
          <WaitingSpinner isLoading={false} message={waitingMessage} compact />
        </>
      ) : isSubmitGame ? (
        <>
          {bonusGameType === 'puzzle' ? (
            <h2
              style={{
                fontSize: 'clamp(1.4rem, 4vw, 2rem)',
                fontWeight: 'bold',
                color: puzzleSolved
                  ? 'var(--color-primary)'
                  : puzzlePendingResult
                    ? '#F8FAFC'
                    : 'rgba(255,255,255,0.6)',
                marginBottom: '1rem',
              }}
            >
              {puzzleSolved
                ? t('bonus.player.puzzleSolved')
                : puzzlePendingResult
                  ? t('bonus.player.answerSubmittedExclaim')
                  : t('bonus.player.puzzleNotSolved')}
            </h2>
          ) : correctAnswer ? (
            <>
              <div style={{ fontSize: '0.95rem', color: 'rgba(255,255,255,0.75)', marginBottom: '0.4rem' }}>{t('bonus.player.correctAnswer')}</div>
              <h2
                style={{
                  fontSize: `clamp(1rem, ${correctAnswerOverlayVw}vw, 2.4rem)`,
                  fontWeight: 'bold',
                  color: 'var(--color-primary)',
                  marginBottom: '1rem',
                  letterSpacing:
                    correctAnswerLen >= 9
                      ? '0.03em'
                      : correctAnswerLen >= 7
                        ? '0.035em'
                        : correctAnswerLen > 14
                          ? '0.02em'
                          : '0.04em',
                  maxWidth: '100%',
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                }}
              >
                {correctAnswer}
              </h2>
            </>
          ) : null}
          {pointsBlock}
          <WaitingSpinner
            isLoading={!roundEnded}
            message={waitingMessage}
            compact
          />
        </>
      ) : showQuickMathChainOverlay ? (
        <>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🌟</div>
          <h2 style={{ fontSize: '1.6rem', fontWeight: 'bold', color: '#F8FAFC', marginBottom: '0.35rem' }}>
            {quickMathPraise.title}
          </h2>
          <p
            style={{
              fontSize: '1rem',
              color: 'rgba(248, 250, 252, 0.82)',
              margin: '0 0 1rem',
              maxWidth: '22rem',
              lineHeight: 1.45,
            }}
          >
            {quickMathPraise.subtitle}
          </p>
          {pointsBlock}
          <WaitingSpinner isLoading={!roundEnded} message={waitingMessage} compact />
        </>
      ) : (
        <>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>{showEarlyResult ? '✅' : '⏰'}</div>
          <h2 style={{ fontSize: '1.6rem', fontWeight: 'bold', color: '#F8FAFC', marginBottom: '0.5rem' }}>
            {longestWordTooShort
              ? t('bonus.player.longestWordTooShortTitle')
              : showEarlyResult
                ? t('bonus.player.answerSubmittedExclaim')
                : t('bonus.player.timesUp')}
          </h2>
          {longestWordTooShort ? (
            <p
              style={{
                fontSize: '1rem',
                color: 'rgba(248, 250, 252, 0.82)',
                margin: '0 0 1rem',
                maxWidth: '22rem',
                lineHeight: 1.45,
              }}
            >
              {t('bonus.player.longestWordTooShortSubtitle')}
            </p>
          ) : null}
          {showLongestWordIdeal ? (
            <div style={{ margin: '0 0 1rem', width: '100%', maxWidth: '22rem' }}>
              <div style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)', marginBottom: '0.2rem' }}>
                {t('bonus.player.longestWordYourWord')}
              </div>
              <div
                style={{
                  fontSize: 'clamp(1.1rem, 5vw, 1.6rem)',
                  fontWeight: 700,
                  color: '#F8FAFC',
                  letterSpacing: '0.06em',
                  marginBottom: '0.85rem',
                  wordBreak: 'break-word',
                }}
              >
                {myFlushedWord.replace(/[^A-Za-z]/g, '').length > 0
                  ? myFlushedWord.toUpperCase()
                  : t('bonus.player.longestWordNoWord')}
              </div>
              <div style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)', marginBottom: '0.2rem' }}>
                {t('bonus.player.longestWordIdealLabel')}
              </div>
              <div
                style={{
                  fontSize: `clamp(1rem, ${correctAnswerOverlayVw}vw, 2.2rem)`,
                  fontWeight: 'bold',
                  color: 'var(--color-primary)',
                  letterSpacing: '0.04em',
                  wordBreak: 'break-word',
                }}
              >
                {correctAnswer}
              </div>
            </div>
          ) : null}
          {pointsBlock}
          <WaitingSpinner
            isLoading={!roundEnded || displayPoints === undefined}
            message={
              displayPoints === undefined && playerSubmitted
                ? t('gameplay.loading.waitingForOthers')
                : waitingMessage
            }
            compact
          />
        </>
      )}
    </div>
  );

  // ── "Bonus Level Finished" + spinner below overlay (only when round fully ended) ──
  const outroFooter = roundEnded && (
    <div
      style={{
        textAlign: 'center',
        padding: '1rem 1rem 0.5rem',
        marginTop: '0.5rem',
        animation: 'fadeIn 0.4s ease-out',
      }}
    >
      <p style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--color-primary)', marginBottom: '0.35rem' }}>
        {t('bonus.player.bonusLevelFinished')}
      </p>
      <WaitingSpinner isLoading size="small" message={t('gameplay.loading.nextQuestion')} compact />
    </div>
  );

  const header = (
    <Header
      leftButton={getPlayerHeaderLeftButton({
        isHost,
        onBackToGameSelection,
        onGoHome,
        backTooltip: t('gameplay.backToGameSelection'),
        homeTooltip: t('gameplay.leaveGame'),
      })}
      rightButton={{
        icon: HeaderIcons.refresh,
        onClick: () => window.location.reload(),
        tooltip: t('common.tooltips.refreshPage'),
      }}
      centerContent={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
          <span style={{ fontSize: '1rem', color: 'var(--text-secondary)', fontWeight: 600 }}>{t('gameplay.roomLabel')}</span>
          <span style={{
            fontSize: '1.2rem',
            fontWeight: 'bold',
            color: 'var(--color-primary)',
            padding: '0.3rem 0.6rem',
            backgroundColor: 'rgba(var(--color-primary-rgb), 0.1)',
            borderRadius: '6px',
            border: '2px solid var(--color-primary)',
          }}>
            {roomState.roomName}
          </span>
        </div>
      }
    />
  );

  if (!payload) {
    return (
      <div className="app">
        {header}
        <div style={{ padding: '2rem' }}>{t('bonus.host.loadingBonus')}</div>
      </div>
    );
  }

  // App already clears the fixed header; modest padding when the sticky timer is hidden.
  // Extra top padding when the result overlay is up so the dark card isn't flush under the header.
  const contentPadTop = showOverlayInsteadOfGame ? '1.25rem' : '0.5rem';
  const isWhosOnImage = payload?.bonusGameType === 'whos-on-image';
  const showWhosOnImageStage =
    isWhosOnImage &&
    hostIsPlaying &&
    !showHostPlayingIntro &&
    !showOverlayInsteadOfGame;
  const bonusPlayerAppClass = [
    'app',
    'app--bonus-player',
    isWhosOnImage ? 'app--whos-on-image' : '',
  ].filter(Boolean).join(' ');

  const whosOnImageStageEl = showWhosOnImageStage ? (
    <div className="whos-on-image-play__stage whos-on-image-play__stage--scroll" aria-hidden>
      <WhosOnImageHost
        imageUrl={payload.imageUrl ?? ''}
        fallbackImageUrl={payload.fallbackImageUrl}
        timeLimit={payload.timeLimit ?? 20000}
        roundEnded={roundEnded}
        fillViewport
      />
    </div>
  ) : null;

  // Host not playing: only the game interaction (no intro, no result on player)
  if (!hostIsPlaying) {
    return (
      <div className={bonusPlayerAppClass}>
        {header}
        <div className="bonus-player__scroll">
          {timerBar}
          <div
          className="container bonus-player-content"
          style={{
            paddingTop: contentPadTop,
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            width: '100%',
            maxWidth: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
            {!showOverlayInsteadOfGame ? gameContent : overlay}
          </div>
          {outroFooter}
        </div>
      </div>
    );
  }

  // Host is playing: during intro show countdown + BonusIntro (same explain as host cast);
  // keep game widgets mounted but hidden so intro-timed games stay synced.
  return (
    <div className={bonusPlayerAppClass}>
      {header}
      <div className="bonus-player__scroll">
        {whosOnImageStageEl}
        {timerBar}
        <div
          className="container bonus-player-content"
          style={{
            paddingTop: isWhosOnImage && !showOverlayInsteadOfGame ? 0 : contentPadTop,
            paddingLeft: isWhosOnImage ? 0 : '1.5rem',
            paddingRight: isWhosOnImage ? 0 : '1.5rem',
            maxWidth: isWhosOnImage ? 'none' : 600,
            margin: '0 auto',
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            zIndex: 1,
          }}
        >
          {!showOverlayInsteadOfGame ? (
            <>
              {hostPlayingIntroBannerEl}
              {hostPlayingIntroExplainEl}
              <div
                style={
                  showHostPlayingIntro
                    ? { display: 'none' }
                    : { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, width: '100%' }
                }
                aria-hidden={showHostPlayingIntro || undefined}
              >
                {gameContent}
              </div>
            </>
          ) : (
            overlay
          )}
        </div>
        {outroFooter}
      </div>
    </div>
  );
};
